// Angular Core and Modules
import {
  Component,
  OnInit,
  Input,
  EventEmitter,
  Output,
  OnChanges,
  SimpleChanges,
  OnDestroy,
  ChangeDetectorRef,
} from '@angular/core';
import {
  UntypedFormBuilder,
  UntypedFormControl,
  UntypedFormGroup,
  Validators,
} from '@angular/forms';
import { MatLegacySelectChange as MatSelectChange } from '@angular/material/legacy-select';
import { MatLegacySnackBar as MatSnackBar } from '@angular/material/legacy-snack-bar';

// RxJS and Operators
import moment from 'moment';
import { Observable, Subject, BehaviorSubject, merge, of } from 'rxjs';
import {
  takeUntil,
  finalize,
  debounceTime,
  switchMap,
  mapTo,
} from 'rxjs/operators';

// Shared Interfaces
import { IAppIntegrationShippingPreset } from '../../../app-integration-account/intefaces/app-integration.interface';
import {
  weightUnits,
  dimensionUnits,
} from '../../../invoice/config/shipstation.config';
import { EWeightUnit } from '../../../invoice/interfaces/shipstation-shipment.interface';
import { IOrderSalesShipstationAddress } from '../../../order/interfaces/order-picklist-preview';
import {
  ESalesOrderStatus,
  ISalesOrderShipment,
} from '../../../order/interfaces/order.interface';
import { IPicklistOrderShipment } from '../../../picklist';
import { SnackBarConfig } from '../../../shared';
import {
  IDimension,
  IWeight,
  OptionInterface,
} from '../../../shared/interfaces/common.interface';
import { IWarehouse } from '../../../shared/interfaces/location-warehouse.interface';

// Configurations

// Feature Specific Interfaces
import { exportB64toPdf } from '../../../shared/util/export.utils';
import {
  trackById,
  trackByValue,
  trackByLabel,
  trackByCode,
} from '../../../shared/util/track-by.util';
import { SystemSettingInterface } from '../../../system-setting/interfaces/system-setting.interface';
import { ShippingLabelFacade } from '../../facade/shipping-label.facade';
import {
  IShippingCarrier,
  IShippingCarrierPackage,
  IShippingCarrierService,
  IShippingItem,
} from '../../interfaces/shipping.interface';

import { IShipstationRates } from './../../interfaces/shipstation.interface';

// Utilities

@Component({
  selector: 'lum-shipping-label-form',
  templateUrl: './shipping-label-form.component.html',
  styleUrls: ['./shipping-label-form.component.scss'],
})
export class ShippingLabelFormComponent implements OnInit, OnChanges, OnDestroy {
  @Input()
  data :{
    shipTo :any;
    shipDate :string;
    salesOrderId :number;
    carrierCode :string;
    serviceCode :string;
    packageCode :string;
    weight :IWeight;
    dimensions :IDimension;
    confirmation :string;
    isBulkUpdate ?:boolean;
    orderStatus ?:string | null;
    showPreview ?:boolean;
    appIntegrationAccountId ?:number;
    salesOrderShipmentId ?:number | null;
    hasMultipleShipments ?:boolean;
    salesOrderShipments ?:ISalesOrderShipment[];
  };

  @Input()
  currentPreset :IAppIntegrationShippingPreset | null = null;

  @Output()
  formSubmitted = new EventEmitter<any>();

  // LOADING INDICATORS
  private readonly _loadingCarriers = new BehaviorSubject<boolean>(false);
  private readonly _loadingPackages = new BehaviorSubject<boolean>(false);
  private readonly _loadingServices = new BehaviorSubject<boolean>(false);
  private readonly _submittingForm = new BehaviorSubject<boolean>(false);
  private readonly _shippingRatesLoading = new BehaviorSubject<boolean>(false);

  get loadingCarriers$() :Observable<boolean> {
    return this._loadingCarriers.asObservable();
  }
  get loadingPackages$() :Observable<boolean> {
    return this._loadingPackages.asObservable();
  }
  get loadingServices$() :Observable<boolean> {
    return this._loadingServices.asObservable();
  }
  get submittingForm$() :Observable<boolean> {
    return this._submittingForm.asObservable();
  }
  get shippingRatesLoading$() :Observable<boolean> {
    return this._shippingRatesLoading.asObservable();
  }

  warehouse :IWarehouse;

  carriers :IShippingCarrier[];
  packages :IShippingCarrierPackage[] = [];
  services :IShippingCarrierService[] = [];

  inventorySettings$ :Observable<SystemSettingInterface>;
  warehouse$ :Observable<IWarehouse>;
  loading$ :Observable<boolean>;

  loadingSettings$ :Observable<boolean>;

  eSalesOrderStatus = ESalesOrderStatus;
  shippingRate :IShipstationRates | null = null;
  shippingRateLoading :boolean = false;

  weightUnits :OptionInterface[] = weightUnits;
  dimensionUnits :OptionInterface[] = dimensionUnits;
  canApplyPreset :boolean = false;

  trackById = trackById;
  trackByValue = trackByValue;
  trackByLabel = trackByLabel;
  trackByCode = trackByCode;

  private onDestroy$ = new Subject();
  private refreshRatesTrigger$ = new Subject<void>();

  private salesOrderIdChanged$ = new BehaviorSubject<number | null>(null);

  form :UntypedFormGroup = this.formBuilder.group({
    carrierCode: ['', Validators.required],
    serviceCode: [''],
    packageCode: [''],
    weight: this.formBuilder.group({
      pounds: this.formBuilder.group({
        units: [EWeightUnit.POUNDS],
        value: [0, [Validators.required]],
      }),
      ounces: this.formBuilder.group({
        units: [EWeightUnit.OUNCES],
        value: [0, [Validators.required]],
      }),
    }),
    dimensions: this.formBuilder.group({
      units: ['inches'],
      length: [0],
      width: [0],
      height: [0],
    }),
    shipTo: [],
    shipFrom: [null],
    shipDate: [, Validators.required],
    salesOrderId: [0],
    confirmation: ['none'],
    carrierServiceId: [null],
    salesOrderShipmentId: [null],
  });

  get weightControl() :UntypedFormGroup {
    return this.form.get('weight') as UntypedFormGroup;
  }

  get packageCodeControl() :UntypedFormControl {
    return this.form.get('packageCode') as UntypedFormControl;
  }

  get serviceCodeControl() :UntypedFormControl {
    return this.form.get('serviceCode') as UntypedFormControl;
  }

  constructor(
    private snackBar :MatSnackBar,
    private formBuilder :UntypedFormBuilder,
    private cd :ChangeDetectorRef,
    private shippingLabelFacade :ShippingLabelFacade,
  ) {}

  ngOnChanges(changes :SimpleChanges) :void {
    if (changes?.currentPreset) {
      this.currentPreset = changes.currentPreset.currentValue;
      // Patching the form with the new preset values
      this.form.patchValue(
        {
          carrierCode: this.currentPreset?.carrierCode,
          confirmation: this.currentPreset?.confirmation,
          weight: {
            pounds:
              this.currentPreset?.weight?.units === 'pounds'
                ? { units: 'pounds', value: this.currentPreset.weight.value }
                : { units: 'pounds', value: 0 },
            ounces:
              this.currentPreset?.weight?.units === 'ounces'
                ? { units: 'ounces', value: this.currentPreset.weight.value }
                : { units: 'ounces', value: 0 },
          },
          dimensions: {
            units: this.currentPreset?.dimensions?.units,
            length: this.currentPreset?.dimensions?.length,
            width: this.currentPreset?.dimensions?.width,
            height: this.currentPreset?.dimensions?.height,
          },
        },
        { emitEvent: false },
      );

      this.getPackages(this.currentPreset?.carrierCode);
      this.getServices(this.currentPreset?.carrierCode);
    }
  }

  ngOnInit() {
    this.patchFormDataInput();

    if (this.data && this.data?.orderStatus === ESalesOrderStatus.SHIPPED) {
      this.form.disable();
    } else {
      this.form.enable();
    }

    this.initDependencies();
    this.listenToFormChanges();
  }

  ngOnDestroy() :void {
    this.onDestroy$.next(null);
    this.onDestroy$.complete();
  }

  onRefreshRate() {
    this.refreshRatesTrigger$.next();
  }

  onSelectCarrier(event :MatSelectChange) :void {
    this.getPackages(event.value);
    this.getServices(event.value);
  }

  onSetUnit(field :string, unit :string) :void {
    this.form.get(field)?.patchValue(unit);
  }

  onSubmit() :void {
    if (this.form.valid) {
      if (this.data.isBulkUpdate) {
        this.formSubmitted.emit(this.form.value);

        return;
      }

      const data = this.form.value;
      const payload = {
        ...data,
        weight: this.weightValue,
      };

      this._submittingForm.next(true);
      this.shippingLabelFacade
        .createShippingLabel(payload)
        .pipe(
          takeUntil(this.onDestroy$),
          finalize(() => {
            this._submittingForm.next(false);
          }),
        )
        .subscribe({
          next: (shipment :IPicklistOrderShipment) => {
            this._submittingForm.next(false);
            if (this.data.showPreview) {
              this.formSubmitted.emit(shipment);

              return;
            }
            const filename = shipment.salesOrderId + 'shipping-label';
            exportB64toPdf(shipment.labelData, filename);
            this.formSubmitted.emit();
          },
          error: (err) => {
            this._submittingForm.next(false);
            this.snackBar.open(
              err.error || 'Failed to create shipping label',
              'Error',
              {
                duration: SnackBarConfig.DEFAULT.duration,
              },
            );
            this.formSubmitted.emit();
          },
        });
    }
  }

  private getRates(
    showRates :boolean = false,
  ) :Observable<IShipstationRates | null> {
    if (this.data?.isBulkUpdate) {
      return of(null);
    }
    const lineItem :IShippingItem = {
      ...this.form.value,
      shipTo: this.shipToAddress,
    };

    return this.shippingLabelFacade.getSortedRates(
      lineItem,
      showRates,
      this.warehouseAddress,
      this.carriers,
    );
  }

  private initDependencies() :void {
    this.getCarriers();
    this.shippingLabelFacade.getDefaultSystemWarehouse().subscribe({
      next: (warehouse) => {
        this.warehouse = warehouse;
        this.form.patchValue(
          {
            shipFrom: {
              ...this.warehouseAddress,
            },
          },
          { emitEvent: false },
        );
      },
    });
  }

  private listenToFormChanges() :void {
    merge(
      this.weightControl.valueChanges.pipe(mapTo(false)),
      this.packageCodeControl.valueChanges.pipe(mapTo(false)),
      this.serviceCodeControl.valueChanges.pipe(mapTo(false)),
      this.refreshRatesTrigger$.pipe(mapTo(true)),
    )
      .pipe(
        debounceTime(500),
        switchMap((forceRefresh) => {
          this._shippingRatesLoading.next(true);

          return this.getRates(forceRefresh);
        }),
        takeUntil(this.onDestroy$),
        finalize(() => this._shippingRatesLoading.next(false)),
      )
      .subscribe({
        next: (rate) => {
          this._shippingRatesLoading.next(false);
          if (rate && rate.serviceCode && rate.carrierCode) {
            this.getPackages(rate.carrierCode);
            this.getServices(rate.carrierCode);

            this.form.patchValue(
              {
                serviceCode: rate.serviceCode,
                carrierCode: rate.carrierCode,
              },
              { emitEvent: false },
            );
            this.shippingRate = rate;
          }
          // Handle the rates
        },
        error: (error) => {
          // Handle error
          this._shippingRatesLoading.next(false);
        },
      });
  }

  private getCarriers() :void {
    this._loadingCarriers.next(true);
    this.shippingLabelFacade
      .loadCarriersData(this.data?.appIntegrationAccountId)
      .pipe(
        takeUntil(this.onDestroy$),
        finalize(() => this._loadingCarriers.next(false)),
      )
      .subscribe({
        next: (response :any) => {
          this.carriers = response;
          this.cd.detectChanges();
        },
      });
  }

  private getPackages(carrierCode :string) :void {
    this._loadingPackages.next(true);
    this.shippingLabelFacade
      .loadPackages(carrierCode, this.data?.salesOrderId)
      .pipe(
        takeUntil(this.onDestroy$),
        finalize(() => this._loadingPackages.next(false)),
      )
      .subscribe({
        next: (response :any) => {
          this.packages = response;
          if (this.packages && this.currentPreset) {
            this.form.patchValue(
              {
                packageCode: this.currentPreset?.packageCode,
              },
              { emitEvent: false },
            );
          }
        },
      });
  }

  private getServices(carrierCode :string) :void {
    this._loadingServices.next(true);
    this.shippingLabelFacade
      .loadServices(carrierCode, this.data?.salesOrderId)
      .pipe(
        takeUntil(this.onDestroy$),
        finalize(() => this._loadingServices.next(false)),
      )
      .subscribe({
        next: (response :any) => {
          this.services = response;

          if (this.services && this.currentPreset) {
            this.form.patchValue(
              {
                serviceCode: this.currentPreset?.serviceCode,
              },
              { emitEvent: false },
            );
          }
        },
      });
  }

  get warehouseAddress() :IOrderSalesShipstationAddress {
    return {
      name: this.warehouse?.location_name,
      company: this.warehouse?.location_name,
      street1: this.warehouse?.street_address_1,
      phone: null,
      postalCode: this.warehouse?.zip,
      city: this.warehouse?.city,
      state: this.warehouse?.state,
      country: this.warehouse?.country,
    } as IOrderSalesShipstationAddress;
  }

  get shipToAddress() :IOrderSalesShipstationAddress {
    return {
      ...this.data.shipTo,
      company: this.data.shipTo?.company || 'N/A',
    };
  }

  get dimensionUnit() :string {
    const unit = this.form.get('dimensions.units')?.value;
    const dimensionUnit = this.dimensionUnits.find((obj) => obj.value === unit);

    return dimensionUnit?.label || '';
  }

  get weightValue() :IWeight {
    const pounds = this.form.get('weight.pounds')?.value;
    const ounces = this.form.get('weight.ounces')?.value;
    if (ounces?.value === null || ounces?.value === 0) {
      return pounds;
    }
    if (pounds?.value > 0) {
      return pounds;
    }

    return ounces;
  }

  private patchFormDataInput() :void {
    const formData :any = { ...this.data };
    formData.shipDate = this.data.shipDate
      ? moment(this.data.shipDate).format('YYYY-MM-DD')
      : moment().format('YYYY-MM-DD');
    this.form.patchValue(formData);
    this.setSalesOrderShipmentIdValidators();

    // Added this methods due to direct form patching not working under staging env
    this.patchWeight();
    this.patchDimensions();
  }

  private setSalesOrderShipmentIdValidators() :void {
    const salesOrderShipmentIdControl = this.form.get(
      'salesOrderShipmentId',
    ) as UntypedFormControl;
    if (this.data.hasMultipleShipments) {
      salesOrderShipmentIdControl.setValidators(Validators.required);
    } else {
      salesOrderShipmentIdControl.clearValidators();
    }
  }

  private patchWeight() :void {
    this.form.patchValue({
      weight: {
        pounds:
          this.data?.weight?.units === EWeightUnit.POUNDS
            ? { units: EWeightUnit.POUNDS, value: this.data.weight.value }
            : { units: 'pounds', value: 0 },
        ounces:
          this.data?.weight?.units === EWeightUnit.OUNCES
            ? { units: EWeightUnit.OUNCES, value: this.data.weight.value }
            : { units: 'ounces', value: 0 },
      },
    });
  }

  private patchDimensions() :void {
    this.form.patchValue({
      dimensions: {
        units: this.data?.dimensions?.units,
        length: this.data?.dimensions?.length,
        width: this.data?.dimensions?.width,
        height: this.data?.dimensions?.height,
      },
    });
  }
}
